import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import {
  deleteComment,
  editComment, getAllAttachment, getDetailByKBNumber, getLatestComment, getUsefulPercentage, getUserComments, getUserRatings,
  getUserUsefuls, mostViewedArticles, provideFeedBack, updateLikeComment,
  updateUnlikeComment, updateViewCount, uploadFeedbackAttachment
} from 'services/knowledgeServices';
import BaseAction from '../actions/baseAction';
import KnowledgeDetailAction, {
  KnowledgeDetailTypes
} from '../actions/knowledgeDetailAction';
import OnBehalfAction from '../actions/onBehalfAction';
import { RootState } from 'redux/store';

function* uploadAttachments(attachments: File[], commentId: string): any {
  try {
    const responses = yield all(
      attachments.map(attachment => {
        const params = {
          kb_feedback_id: commentId,
          file_name: attachment.name,
          type: attachment.type,
        };
        return call(uploadFeedbackAttachment, attachment, params);
      }),
    );
    for (const res of responses) {
      if (!res.success) {
        yield put(
          BaseAction.errorHandling({
            message: `UPLOAD_FEEDBACK_ATTACHMENT ${res.message}`,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `UPLOAD_FEEDBACK_ATTACHMENT ${error}`,
      }),
    );
  }
}

export function* fetchKbDetail(action: any): any {
  const { kbNumber, userName, lang } = action;
  
  try {
    const result = yield call(getDetailByKBNumber, kbNumber, userName, lang);
    if (result.status === 200) {
      const data = result.data;
      const xTotalCount = result.headers['x-total-count'];
      const totalCount = Number(xTotalCount);
      if (!!data && totalCount > 0) {
        yield put(OnBehalfAction.uptimeAuditLog({
          action: 'VIEW',
          type: 'KB',
          detail: data.number,
        }));
        yield put(KnowledgeDetailAction.fetchKbDetailSuccess(data));
      } else {
        yield put(KnowledgeDetailAction.fetchKbDetailSuccess({ id: null, totalCount: totalCount }));
      }
    } else {
      yield put(
        BaseAction.errorHandling({
          message: `FETCH_KB_DETAIL ${result.error}`,
        }),
      );
    }
  } catch (error) {
    yield put(KnowledgeDetailAction.setLoadingKB(false));
    yield put(
      BaseAction.errorHandling({
        message: `FETCH_KB_DETAIL ${error}`,
      }),
    );
  }
}

export function* fetchLatestComment(action: any): any {
  try {
    const state: RootState = yield select();
    const { configReducer } = state;
    const { generalConfig } = configReducer;

    const isFetchLatestCommentByKbNumber = generalConfig?.isFetchLatestCommentByKbNumber || false;

    const params = {...(isFetchLatestCommentByKbNumber ? {kb_number: action.kb_uid} : {kb_uid: action.kb_uid})};

    const result = yield call(getLatestComment, params);
    if (!!result) {
      yield put(KnowledgeDetailAction.fetchLatestCommentSuccess(result));
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `FETCH_LATEST_COMMENT ${error}`,
      }),
    );
  }
}

export function* fetchCommentsData(action: any): any {
  try {    
    const result = yield call(getUserComments, action.params);
    if (!!result) {
      yield put(KnowledgeDetailAction.fetchCommentsDataSuccess(result));
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `FETCH_COMMENTS_DATA ${error}`,
      }),
    );
  }
}

export function* provideCommentFeedback(action: any): any {
  try {
    yield put(KnowledgeDetailAction.setLoadingKB(true));
    yield put(KnowledgeDetailAction.setLoadingFeedback(true));
    yield put(KnowledgeDetailAction.setLoadingComment(true));
    const { attachments, ...params } = action.params;
    const result = yield call(provideFeedBack, params);
    if (result.status === 200) {
      const sysId = result.data.data?.sys_id;
      yield call(uploadAttachments, attachments, sysId);
      yield put(KnowledgeDetailAction.reloadCommentsDataSuccess());
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `PROVIDE_COMMENT_FEEDBACK ${error}`,
      }),
    );
  } finally {
    yield put(KnowledgeDetailAction.setLoadingKB(false));
    yield put(KnowledgeDetailAction.setLoadingFeedback(false));
    yield put(KnowledgeDetailAction.setOpenFeedbackDialog(false));
    yield put(KnowledgeDetailAction.setLoadingComment(false));
  }
}

export function* provideLikeComment(action: any): any {
  try {
    let result = null;
    if (!!action.like) {
      result = yield call(updateLikeComment, action.username, action.comment.uid);
    } else {
      result = yield call(updateUnlikeComment, action.comment.liked?.uid);
    }
    if (result.status === 200) {
      yield put(KnowledgeDetailAction.reloadCommentsDataSuccess());
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `PROVIDE_LIKE_COMMENT ${error}`,
      }),
    );
  }
}

export function* provideCommentDeleted(action: any): any {
  try {
    yield put(KnowledgeDetailAction.setLoadingKB(true));
    const result = yield call(deleteComment, action.commentId);
    if (result.status === 200) {
      yield put(KnowledgeDetailAction.reloadCommentsDataSuccess());
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `PROVIDE_COMMENT_DELETED ${error}`,
      }),
    );
  } finally {
    yield put(KnowledgeDetailAction.setLoadingKB(false));
  }
}

export function* provideCommentEdited(action: any): any {
  try {
    const { content } = action.paramsObj;
    yield put(KnowledgeDetailAction.setLoadingKB(true));
    const result = yield call(editComment, action.commentId, content);
    if (result.status === 200) {
      // yield call(uploadAttachments, attachments, action.commentId);
      yield put(KnowledgeDetailAction.reloadCommentsDataSuccess());
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `PROVIDE_COMMENT_EDITED ${error}`,
      }),
    );
  } finally {
    yield put(KnowledgeDetailAction.setLoadingKB(false));
  }
}

export function* getKbUsefulPercentage(action: any): any {
  try {
    const result = yield call(getUsefulPercentage, action.params);
    yield put(KnowledgeDetailAction.getKbUsefulPercentageSuccess(result?.percentage || 0));
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `GET_KB_USEFUL_PERCENTAGE ${error}`
    }));
  }
}

export function* getUsefulByUser(action: any): any {
  try {
    const result = yield call(getUserUsefuls, action.params);
    if (result.data.length > 0) {
      yield put(KnowledgeDetailAction.getUsefulByUserSuccess(result.data[0]?.useful.toLowerCase()));
    } else {
      yield put(KnowledgeDetailAction.getUsefulByUserSuccess(''));
    }
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `GET_USEFUL_BY_USER ${error}`
    }));
  }
}

export function* fetchRatings(action: any): any {
  try {
    const result = yield call(getUserRatings, action.params);
    const rating = Number(result.data?.[0]?.rating) || 0;
    yield put(KnowledgeDetailAction.fetchRatingsSuccess(rating));
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `FETCH_RATINGS ${error}`
    }));
  }
}

export function* increaseViewCount(action: any): any {
  try {
    yield call(updateViewCount, action.userName, action.id);
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `UPDATE_VIEW_COUNT ${error}`
    }));
  }
}

export function* updateUsefulChange(action: any): any {
  const { usefulChange, params, id } = action;
  yield put(KnowledgeDetailAction.updateUsefulChangeSuccess(usefulChange));
  try {
    yield put(KnowledgeDetailAction.setLoadingFeedback(true));
    const result = yield call(provideFeedBack, params);
    if (result.status === 200) {
      yield put(KnowledgeDetailAction.getKbUsefulPercentage({kb_uid: id}));
      if(result?.data?.enableFeedbackComment === true){
        yield put(KnowledgeDetailAction.reloadCommentsDataSuccess());
      }
    }
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `UPDATE_USEFUL_CHANGE ${error}`
    }));
  } finally {
    yield put(KnowledgeDetailAction.setLoadingFeedback(false));
    yield put(KnowledgeDetailAction.setOpenFeedbackDialog(false));
  }
}

export function* updateRatingChange(action: any): any {
  const { ratingChange, params, kbNumber, userName, timeZone } = action;
  yield put(KnowledgeDetailAction.updateRatingChangeSuccess(ratingChange));
  try {
    yield put(KnowledgeDetailAction.setLoadingFeedback(true));
    const result = yield call(provideFeedBack, params);
    if (result.status === 200) {
      yield put(KnowledgeDetailAction.fetchKbDetail(kbNumber, userName, timeZone, true));
      if(result?.data?.enableFeedbackComment === true){
        yield put(KnowledgeDetailAction.reloadCommentsDataSuccess());
      }
    }
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `UPDATE_RATING_CHANGE ${error}`
    }));
  } finally {
    yield put(KnowledgeDetailAction.setLoadingFeedback(false));
    yield put(KnowledgeDetailAction.setOpenFeedbackDialog(false));
  }
}

export function* getMostViewedArticles(action: any): any {
  try {
    const result = yield call(mostViewedArticles, action.params);
    if (result?.data?.data.length > 0) {
      yield put(KnowledgeDetailAction.getMostViewedArticlesSuccess(result?.data.data));
    } else {
      yield put(KnowledgeDetailAction.getMostViewedArticlesSuccess([]));
    }
  } catch (error) {
    yield put(BaseAction.errorHandling({
      message: `GET_MOST_VIEWED_ARTICLES ${error}`
    }));
  }
}

export function* getAllAttachments(action: any): any {
  try {
    const result = yield call(getAllAttachment, action.kbNumber);
    if (result) {
      yield put(KnowledgeDetailAction.fetchAllAttachmentSuccess(result));
    }
  } catch (error) {
    yield put(
      BaseAction.errorHandling({
        message: `FETCH_ALL_ATTACHMENT ${error}`,
      }),
    );
  }

}

export const watchKnowledgeDetail = [
  takeLatest(KnowledgeDetailTypes.FETCH_KB_DETAIL, fetchKbDetail),
  takeLatest(KnowledgeDetailTypes.GET_KB_USEFUL_PERCENTAGE, getKbUsefulPercentage),
  takeLatest(KnowledgeDetailTypes.GET_USEFUL_BY_USER, getUsefulByUser),
  takeLatest(KnowledgeDetailTypes.FETCH_RATINGS, fetchRatings),
  takeLatest(KnowledgeDetailTypes.UPDATE_VIEW_COUNT, increaseViewCount),
  takeLatest(KnowledgeDetailTypes.UPDATE_USEFUL_CHANGE, updateUsefulChange),
  takeLatest(KnowledgeDetailTypes.UPDATE_RATING_CHANGE, updateRatingChange),
  takeLatest(KnowledgeDetailTypes.FETCH_LATEST_COMMENT, fetchLatestComment),
  takeLatest(KnowledgeDetailTypes.FETCH_COMMENTS_DATA, fetchCommentsData),
  takeLatest(KnowledgeDetailTypes.PROVIDE_COMMENT_FEEDBACK, provideCommentFeedback),
  takeLatest(KnowledgeDetailTypes.PROVIDE_LIKE_COMMENT, provideLikeComment),
  takeLatest(KnowledgeDetailTypes.PROVIDE_COMMENT_DELETED, provideCommentDeleted),
  takeLatest(KnowledgeDetailTypes.PROVIDE_COMMENT_EDITED, provideCommentEdited),
  takeLatest(KnowledgeDetailTypes.GET_MOST_VIEWED_ARTICLES, getMostViewedArticles),
  takeLatest(KnowledgeDetailTypes.FETCH_ALL_ATTACHMENT, getAllAttachments),
];
